import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  Upload,
  ArrowLeft,
  Save,
  Trash2,
  Map as MapIcon,
  RotateCcw,
  ZoomIn,
  ZoomOut,
  MapPin,
  Star,
  ChevronDown,
  ChevronUp,
  Bell,
  Pencil,
  X,
  Maximize2,
  Minimize2,
  Play,
  Square,
  Move,
  Wand2,
} from 'lucide-react';
import { useSelector } from 'react-redux';
import type { RootState } from '../store/store';
import { ZonePlanApiService } from '../services/zonePlanApi';
import { useGetAlarmLogQuery, useGetAlarmsQuery } from '../services/alarmApi';
import { useGetLiveDataQuery } from '../services/sensorDataApi';
import { normalizeAlarmType } from '../utils/alarmDisplay';
import { filterAlarmLogsBySensorAccess } from '../utils/alarmAccess';
import {
  computePlanLabelLayouts,
  DEFAULT_PLAN_LABEL_LAYOUT,
  hasManualLabelOffset,
  layoutFromManualOffset,
  type PlanSensorLabelLayout,
} from '../utils/planLabelLayout';

const API_BASE = 'https://s3.ts1cloud.com/service3';
const getHeaders = (user: any) => ({
  'Content-Type': 'application/json',
  'x-company-id': user?.companyID?.toString() || '',
  'x-company-name': user?.companyName || '',
});
const getFavouriteKey = (user: any) => `planview_fav_${user?.companyName ?? ''}_${user?.id ?? ''}`;
const getLastZoneKey  = (user: any) => `planview_last_${user?.companyName ?? ''}_${user?.id ?? ''}`;
const zonePlanApi = new ZonePlanApiService();

/** iPad / iPhone Safari: native element fullscreen often fails or does not resize; pseudo-fullscreen is reliable. */
function isIosLikeDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ can report as Mac with touch
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;
  return false;
}

function getDocFullscreenEl(): Element | null {
  return (
    document.fullscreenElement ??
    (document as unknown as { webkitFullscreenElement?: Element | null }).webkitFullscreenElement ??
    null
  );
}

/** `:fullscreen` + WebKit equivalents so Safari/iPad expands the shell to the viewport. */
const planFullscreenCss = `
.plan-view-fs-root:fullscreen {
  width: 100vw !important;
  height: 100vh !important;
  height: 100dvh !important;
  min-height: 100vh !important;
  max-height: 100dvh !important;
  box-sizing: border-box;
}
.plan-view-fs-root:-webkit-full-screen {
  width: 100vw !important;
  height: 100vh !important;
  height: 100dvh !important;
  min-height: 100vh !important;
  max-height: 100dvh !important;
  box-sizing: border-box;
}
`;
if (typeof document !== 'undefined' && !document.getElementById('plan-view-fs-styles')) {
  const styleFs = document.createElement('style');
  styleFs.id = 'plan-view-fs-styles';
  styleFs.innerHTML = planFullscreenCss;
  document.head.appendChild(styleFs);
}

/** Same flashing bell animations as SensorTable (shared id avoids duplicate injections). */
const planBellCss = `
@keyframes bell-flash-red { 0%, 100% { color: #ef4444; opacity: 1; } 50% { color: #b91c1c; opacity: 0.3; } }
@keyframes bell-flash-blue { 0%, 100% { color: #3b82f6; opacity: 1; } 50% { color: #1e40af; opacity: 0.3; } }
@keyframes bell-flash-orange { 0%, 100% { color: #f59e42; opacity: 1; } 50% { color: #b45309; opacity: 0.3; } }
.bell-flash-red { animation: bell-flash-red 1s infinite; }
.bell-flash-blue { animation: bell-flash-blue 1s infinite; }
.bell-flash-orange { animation: bell-flash-orange 1s infinite; }
`;
if (typeof document !== 'undefined' && !document.getElementById('bell-flash-styles')) {
  const style = document.createElement('style');
  style.id = 'bell-flash-styles';
  style.innerHTML = planBellCss;
  document.head.appendChild(style);
}

function sensorIdsMatch(a: unknown, b: unknown): boolean {
  if (a == null || b == null) return false;
  if (a === b) return true;
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb) && na === nb) return true;
  return String(a).trim() === String(b).trim();
}

/**
 * Live rows use `sensor_id` as serial in most deployments; some UIs also match DB id.
 * Mirror Dashboard/SensorTable (numeric serial) and tolerate dotted serials.
 */
function pickLiveRow(liveRows: any[], sensor: { id: string; serialNo?: string | number | null }): any | undefined {
  if (!liveRows?.length) return undefined;
  return liveRows.find((l: any) => liveRowMatchesSensor(l, sensor));
}

function liveRowMatchesSensor(l: any, sensor: { id: string; serialNo?: string | number | null }): boolean {
  const liveId = l?.sensor_id;
  const serial = sensor.serialNo;
  const serialNorm = serial != null && serial !== '' ? String(serial).trim().replace(/\./g, '') : '';
  const liveStr = liveId != null ? String(liveId).trim() : '';
  if (serialNorm) {
    const liveNorm = liveStr.replace(/\./g, '');
    if (liveNorm === serialNorm) return true;
    const liveNum = Number(liveNorm);
    const serialNum = Number(serialNorm);
    if (!Number.isNaN(liveNum) && !Number.isNaN(serialNum) && liveNum === serialNum) return true;
  }
  if (sensorIdsMatch(liveId, sensor.id)) return true;
  return false;
}

/** Alarm API sometimes returns `{ data: [...] }` or similar — other pages guard with Array.isArray. */
function normalizeAlarmLog(raw: unknown): any[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    for (const k of ['data', 'logs', 'alarmLog', 'records', 'items', 'result']) {
      const v = o[k];
      if (Array.isArray(v)) return v;
    }
  }
  return [];
}

function alarmEntrySensorId(log: any): unknown {
  if (!log || typeof log !== 'object') return undefined;
  return log.sensor_id ?? log.SensorID ?? log.SensorId ?? log.sensorId;
}

function truthyAcknowledged(v: unknown): boolean {
  if (v === true || v === 1 || v === '1') return true;
  const s = String(v ?? '').toLowerCase();
  return s === 'true' || s === 'yes';
}

/** Active alarm row (`active` varies by backend: boolean, 1/0, strings). */
function truthyAlarmActive(v: unknown): boolean {
  if (v === true || v === 1 || v === '1') return true;
  const s = String(v ?? '').toLowerCase();
  return s === 'true' || s === 'active';
}

function hasAlarmResetValue(v: unknown): boolean {
  if (v == null || v === false || v === '' || v === 0 || v === '0') return false;
  return true;
}

function newestAlarmByActivated(rows: any[]): any | null {
  if (!rows.length) return null;
  return rows.reduce((a, b) =>
    new Date(a.activated || 0).getTime() >= new Date(b.activated || 0).getTime() ? a : b
  );
}

function logsForSensorDbId(logs: any[], sensorDbId: string): any[] {
  return logs.filter((log: any) => sensorIdsMatch(alarmEntrySensorId(log), sensorDbId));
}

function normalizeAlarmsConfigList(raw: unknown): any[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    for (const k of ['data', 'alarms', 'items', 'result']) {
      const v = o[k];
      if (Array.isArray(v)) return v as any[];
    }
  }
  return [];
}

function truthyThresholdEnable(v: unknown): boolean {
  if (v === true || v === 1 || v === '1') return true;
  const s = String(v ?? '').toLowerCase();
  return s === 'true' || s === 'yes' || s === 'on';
}

/**
 * Prefer alarm-log state (matches SensorTable: active rows, then pre-alarm rows).
 * If the log does not reflect the condition yet, fall back to configured thresholds vs live temp (dashboard-style).
 */
function resolveSensorPlanAlarm(
  sensorDbId: string,
  rawAlarmLog: unknown,
  alarmsFromApi: unknown,
  sensor: { temp?: number; status?: string },
): any | null {
  const logs = normalizeAlarmLog(rawAlarmLog);
  const rows = logsForSensorDbId(logs, sensorDbId);

  const activeRows = rows.filter(
    (log: any) =>
      truthyAlarmActive(log.active) &&
      !truthyAcknowledged(log.acknowledged) &&
      !hasAlarmResetValue(log.alarm_reset),
  );
  if (activeRows.length) {
    return { ...newestAlarmByActivated(activeRows)!, _planAlarmKind: 'log-active' as const };
  }

  const preRows = rows.filter(
    (log: any) =>
      !truthyAlarmActive(log.active) &&
      !truthyAcknowledged(log.acknowledged) &&
      !hasAlarmResetValue(log.alarm_reset),
  );
  if (preRows.length) {
    return { ...newestAlarmByActivated(preRows)!, _planAlarmKind: 'log-pre' as const, preAlarm: true };
  }

  const online = sensor.status === 'online';
  const temp = Number(sensor.temp);
  const cfgList = normalizeAlarmsConfigList(alarmsFromApi);
  if (!online || Number.isNaN(temp) || !cfgList.length) return null;

  const cfg = cfgList.find((a: any) => sensorIdsMatch(a.sensor_id ?? a.sensorId, sensorDbId));
  if (!cfg || cfg.alarms_disabled) return null;

  const hiRaw = cfg.high_alarm;
  const loRaw = cfg.low_alarm;
  const hi = hiRaw === '' || hiRaw == null ? NaN : Number(hiRaw);
  const lo = loRaw === '' || loRaw == null ? NaN : Number(loRaw);

  if (truthyThresholdEnable(cfg.high_enable)) {
    if (!Number.isNaN(hi) && temp >= hi) {
      return { alarm_type: 'high', _planAlarmKind: 'threshold' as const, thresholdOnly: true };
    }
  }
  if (truthyThresholdEnable(cfg.low_enable)) {
    if (!Number.isNaN(lo) && temp <= lo) {
      return { alarm_type: 'low', _planAlarmKind: 'threshold' as const, thresholdOnly: true };
    }
  }

  return null;
}

// ─── Sensor card ──────────────────────────────────────────────────────────────

interface SitePlanSensorProps {
  sensor: {
    id: string; name: string; temp: number; humidity: number;
    status: string; x: number; y: number; battery?: string; rssi?: string;
    labelCenterOffsetX?: number | null;
    labelCenterOffsetY?: number | null;
  };
  latestAlarm?: any | null;
  onDrag: (id: string, relX: number, relY: number) => void;
  onLabelDrag: (id: string, centerOffsetX: number, centerOffsetY: number) => void;
  onResetLabel: (id: string) => void;
  onDelete: (id: string) => void;
  isSelected: boolean;
  onSelect: (id: string) => void;
  zoom: number;
  editMode: boolean;
  canvasSize: { w: number; h: number };
  onNavigateToGraph: (sensorId: string) => void;
  isHighlighted?: boolean;
  labelLayout?: PlanSensorLabelLayout;
  planOpacity?: number;
}

const SitePlanSensor: React.FC<SitePlanSensorProps> = ({
  sensor, latestAlarm, onDrag, onLabelDrag, onResetLabel, onDelete, isSelected, onSelect, zoom, editMode, canvasSize, onNavigateToGraph, isHighlighted, labelLayout = DEFAULT_PLAN_LABEL_LAYOUT, planOpacity = 1,
}) => {
  const [isPinDragging, setIsPinDragging] = useState(false);
  const [isLabelDragging, setIsLabelDragging] = useState(false);
  const [dragStartScreen, setDragStartScreen] = useState({ x: 0, y: 0 });
  const [dragStartRel, setDragStartRel] = useState({ x: 0, y: 0 });
  const [labelDragStart, setLabelDragStart] = useState({ x: 0, y: 0, offsetX: 0, offsetY: 0 });

  const handlePinMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!editMode) return;
    onSelect(sensor.id);
    setIsPinDragging(true);
    setDragStartScreen({ x: e.clientX, y: e.clientY });
    setDragStartRel({ x: sensor.x, y: sensor.y });
  };

  const handleLabelMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!editMode) return;
    onSelect(sensor.id);
    setIsLabelDragging(true);
    setLabelDragStart({
      x: e.clientX,
      y: e.clientY,
      offsetX: labelLayout.centerOffsetX,
      offsetY: labelLayout.centerOffsetY,
    });
  };

  useEffect(() => {
    if (!isPinDragging) return;
    const onMove = (e: MouseEvent) => {
      const newRelX = dragStartRel.x + (e.clientX - dragStartScreen.x) / (zoom * canvasSize.w);
      const newRelY = dragStartRel.y + (e.clientY - dragStartScreen.y) / (zoom * canvasSize.h);
      onDrag(sensor.id, Math.max(0, Math.min(1, newRelX)), Math.max(0, Math.min(1, newRelY)));
    };
    const onUp = () => setIsPinDragging(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isPinDragging, dragStartScreen, dragStartRel, sensor.id, onDrag, zoom, canvasSize]);

  useEffect(() => {
    if (!isLabelDragging) return;
    const onMove = (e: MouseEvent) => {
      const dx = (e.clientX - labelDragStart.x) / zoom;
      const dy = (e.clientY - labelDragStart.y) / zoom;
      onLabelDrag(
        sensor.id,
        labelDragStart.offsetX + dx,
        labelDragStart.offsetY + dy,
      );
    };
    const onUp = () => setIsLabelDragging(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isLabelDragging, labelDragStart, sensor.id, onLabelDrag, zoom]);

  const isOnline = sensor.status === 'online';
  const isOutOfService = sensor.status === 'out-of-service';
  const isOffline = !isOnline && !isOutOfService;
  const temp = sensor.temp ?? 0;

  const preAlarm = !!(latestAlarm as any)?.preAlarm;
  const rawType = latestAlarm ? String((latestAlarm as any).alarm_type) : '';
  const ak = rawType ? normalizeAlarmType(rawType) : null;
  const isAlarmLow = ak === 'low' || rawType === '2';
  const isAlarmHigh = ak === 'high' || rawType === '1';
  const isAlarmBattery = ak === 'battery' || rawType === '4';
  const isAlarmStale = ak === 'missing_data' || rawType === '5';

  const tempColor = isOutOfService ? 'text-gray-500'
    : preAlarm ? 'text-orange-400'
    : latestAlarm && isAlarmLow ? 'text-blue-400'
    : latestAlarm && isAlarmHigh ? 'text-red-400'
    : latestAlarm && isAlarmBattery ? 'text-rose-400'
    : latestAlarm && isAlarmStale ? 'text-violet-400'
    : latestAlarm ? 'text-red-400'
    : !isOnline ? 'text-amber-500'
    : 'text-emerald-600';

  const borderColor = isOutOfService ? 'border-gray-600'
    : preAlarm ? 'border-orange-500'
    : latestAlarm && isAlarmLow ? 'border-blue-500'
    : latestAlarm && isAlarmHigh ? 'border-red-500'
    : latestAlarm && isAlarmBattery ? 'border-rose-500'
    : latestAlarm && isAlarmStale ? 'border-violet-500'
    : latestAlarm ? 'border-red-500'
    : !isOnline ? 'border-amber-500'
    : 'border-emerald-600';

  const glowColor = preAlarm ? 'shadow-orange-900/50'
    : latestAlarm && isAlarmLow ? 'shadow-blue-900/50'
    : latestAlarm && isAlarmHigh ? 'shadow-red-900/60'
    : latestAlarm && isAlarmBattery ? 'shadow-rose-900/50'
    : latestAlarm && isAlarmStale ? 'shadow-violet-900/50'
    : latestAlarm ? 'shadow-red-900/60'
    : isOnline ? 'shadow-emerald-900/40'
    : isOffline ? 'shadow-amber-900/30'
    : '';

  const cardHint = latestAlarm
    ? (preAlarm ? 'Pre-alarm countdown — open chart for details' : 'Active alarm or threshold breach — open chart')
    : editMode
      ? 'Drag pin to move · drag label to adjust leader line · double-click opens chart'
      : 'Open temperature chart';

  const isDragging = isPinDragging || isLabelDragging;
  const manualLabel = hasManualLabelOffset(sensor);

  const connectorStroke = isOffline
    ? 'rgba(217, 119, 6, 0.75)'
    : isOutOfService || !isOnline
      ? 'rgba(75, 85, 99, 0.85)'
      : preAlarm
        ? 'rgba(234, 88, 12, 0.85)'
        : latestAlarm && isAlarmLow
          ? 'rgba(37, 99, 235, 0.85)'
          : latestAlarm
            ? 'rgba(220, 38, 38, 0.85)'
            : 'rgba(4, 120, 87, 0.8)';

  const connectorPointsStr = labelLayout.connectorPoints
    .map((p) => `${p.x},${p.y}`)
    .join(' ');

  // Switch name card header between white-bg/dark-text and dark-bg/white-text.
  // A hard snap avoids both passing through mid-grey simultaneously (which made both unreadable).
  const nameDark = planOpacity < 0.55;
  const nameBg = nameDark ? 'rgba(17,24,39,0.97)' : 'rgba(255,255,255,0.97)';
  const nameTextColor = nameDark ? '#f9fafb' : '#111827';

  return (
    <div
      data-sensor-card
      title={cardHint}
      className={`absolute select-none ${isHighlighted ? 'z-30' : isSelected ? 'z-20' : 'z-10'}`}
      style={{
        left: `${sensor.x * 100}%`,
        top: `${sensor.y * 100}%`,
        transition: isDragging ? 'none' : 'transform 0.15s ease',
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (editMode) onSelect(sensor.id);
        else onNavigateToGraph(sensor.id);
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (editMode) onNavigateToGraph(sensor.id);
      }}
    >
      <div
        className="relative"
        style={{
          transform: `translate(-50%, 0) scale(${isDragging ? 1.07 : isSelected ? 1.03 : 1})`,
          transformOrigin: 'center bottom',
        }}
      >
        {/* Leader line — extends or bends when labels would overlap */}
        <svg
          className="absolute left-0 top-0 overflow-visible pointer-events-none"
          width={1}
          height={1}
          aria-hidden
        >
          <polyline
            points={connectorPointsStr}
            fill="none"
            stroke={connectorStroke}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        {/* Label card — draggable in edit mode to position leader line */}
        <div
          className={`absolute ${editMode && isSelected ? 'cursor-grab active:cursor-grabbing' : ''}`}
          style={{
            left: labelLayout.centerOffsetX,
            top: labelLayout.centerOffsetY,
            transform: 'translate(-50%, -50%)',
            transition: isLabelDragging ? 'none' : 'left 0.15s ease, top 0.15s ease',
          }}
          onMouseDown={editMode ? handleLabelMouseDown : undefined}
        >
          {isHighlighted && (
            <div
              className="absolute pointer-events-none animate-ping"
              style={{ inset: -8, borderRadius: 12, border: '3px solid #60a5fa', opacity: 0.75 }}
            />
          )}

          {editMode && isSelected && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDelete(sensor.id); }}
                className="absolute -top-2.5 -right-2.5 w-5 h-5 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center z-30 shadow-lg"
                title="Remove from plan"
              >
                <Trash2 className="w-3 h-3 text-white" />
              </button>
              {manualLabel && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onResetLabel(sensor.id); }}
                  className="absolute -top-2.5 -left-2.5 z-30 rounded-md bg-gray-800/95 border border-gray-600 px-1.5 py-0.5 text-[9px] font-medium text-gray-200 hover:bg-gray-700 shadow-lg whitespace-nowrap"
                  title="Reset label to default position"
                >
                  Reset line
                </button>
              )}
              <div
                className="absolute -bottom-2 left-1/2 -translate-x-1/2 z-30 rounded-full bg-indigo-600/90 p-0.5 shadow-md pointer-events-none"
                title="Drag label"
              >
                <Move className="w-3 h-3 text-white" />
              </div>
            </>
          )}

          {latestAlarm && !isOutOfService && (
            <div className="absolute -top-1 -left-1 z-[22] pointer-events-none" title={preAlarm ? 'Pre-alarm' : 'Alarm'}>
              {preAlarm ? (
                <Bell className="w-3.5 h-3.5 bell-flash-orange text-orange-400 drop-shadow-md" />
              ) : isAlarmLow ? (
                <Bell className="w-3.5 h-3.5 bell-flash-blue text-blue-400 drop-shadow-md" />
              ) : isAlarmHigh ? (
                <Bell className="w-3.5 h-3.5 bell-flash-red text-red-400 drop-shadow-md" />
              ) : isAlarmBattery ? (
                <Bell className="w-3.5 h-3.5 bell-flash-orange text-rose-400 drop-shadow-md" />
              ) : isAlarmStale ? (
                <Bell className="w-3.5 h-3.5 bell-flash-orange text-violet-400 drop-shadow-md" />
              ) : (
                <Bell className="w-3.5 h-3.5 text-red-400 animate-pulse drop-shadow-md" />
              )}
            </div>
          )}

          {isOnline && !latestAlarm && (
            <div className="absolute -top-1 -right-1 w-3 h-3 z-20 pointer-events-none">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-600 opacity-60 animate-ping" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-600" />
            </div>
          )}
          {isOnline && latestAlarm && preAlarm && (
            <div className="absolute -top-1 -right-1 w-3 h-3 z-20 pointer-events-none">
              <span className="absolute inline-flex h-full w-full rounded-full bg-orange-500 opacity-60 animate-ping" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-400" />
            </div>
          )}
          {isOnline && latestAlarm && !preAlarm && isAlarmLow && (
            <div className="absolute -top-1 -right-1 w-3 h-3 z-20 pointer-events-none">
              <span className="absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-60 animate-ping" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500" />
            </div>
          )}
          {isOnline && latestAlarm && !preAlarm && isAlarmHigh && (
            <div className="absolute -top-1 -right-1 w-3 h-3 z-20 pointer-events-none">
              <span className="absolute inline-flex h-full w-full rounded-full bg-red-600 opacity-60 animate-ping" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
            </div>
          )}
          {isOnline && latestAlarm && !preAlarm && isAlarmBattery && (
            <div className="absolute -top-1 -right-1 w-3 h-3 z-20 pointer-events-none">
              <span className="absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-60 animate-ping" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-rose-500" />
            </div>
          )}
          {isOnline && latestAlarm && !preAlarm && isAlarmStale && (
            <div className="absolute -top-1 -right-1 w-3 h-3 z-20 pointer-events-none">
              <span className="absolute inline-flex h-full w-full rounded-full bg-violet-500 opacity-60 animate-ping" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-violet-500" />
            </div>
          )}
          {isOnline && latestAlarm && !preAlarm && !isAlarmLow && !isAlarmHigh && !isAlarmBattery && !isAlarmStale && (
            <div className="absolute -top-1 -right-1 w-3 h-3 z-20 pointer-events-none">
              <span className="absolute inline-flex h-full w-full rounded-full bg-red-600 opacity-60 animate-ping" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
            </div>
          )}
          {isOffline && (
            <div className="absolute -top-1 -right-1 w-3 h-3 z-20 pointer-events-none">
              <span className="absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500" />
            </div>
          )}

          <div
            className={`relative flex flex-col items-stretch min-w-[4.5rem] max-w-[min(15rem,calc(100vw-2rem))] rounded-xl overflow-hidden border-2 ${borderColor} shadow-xl ${glowColor} ${editMode && isSelected ? 'ring-2 ring-indigo-400/60' : ''} ${editMode ? '' : 'cursor-pointer hover:brightness-105'} ${editMode && isSelected ? '' : editMode ? 'cursor-pointer' : ''}`}
          >
            <div className="px-1.5 py-1 text-center border-b border-gray-900/15 shadow-[0_1px_0_rgba(0,0,0,0.06)]" style={{ backgroundColor: nameBg }}>
              <div
                className="text-[10px] sm:text-[11px] font-semibold leading-snug normal-case break-words hyphens-auto"
                style={{ color: nameTextColor }}
                lang="en"
              >
                {sensor.name}
              </div>
            </div>
            <div className="bg-gray-950/96 backdrop-blur-sm px-2 py-1.5 text-center">
              <div className={`${tempColor} text-[22px] font-bold leading-none`}>
                {isOnline ? `${Number(temp).toFixed(1)}°` : '—'}
              </div>
              <div className={`text-[9px] leading-tight mt-0.5 font-medium ${isOffline ? 'text-amber-500' : isOutOfService ? 'text-gray-500' : 'text-gray-500'}`}>
                {isOffline && 'OFFLINE'}
                {isOutOfService && 'Out of service'}
                {isOnline && latestAlarm && !isOutOfService && (
                  <span className={`font-semibold ${preAlarm ? 'text-orange-400' : isAlarmLow ? 'text-blue-400' : 'text-red-400'}`}>
                    {preAlarm ? 'PRE-ALARM' : 'ALARM'}
                    {sensor.humidity > 0 ? ' · ' : ''}
                  </span>
                )}
                {isOnline && sensor.humidity > 0 && `${sensor.humidity}% rh`}
              </div>
            </div>
          </div>
        </div>

        {/* Pin dot — fixed on the map anchor; drag to move sensor */}
        <div
          role="presentation"
          onMouseDown={handlePinMouseDown}
          className={`absolute left-0 top-0 w-4 h-4 -translate-x-1/2 -translate-y-full flex items-end justify-center ${editMode ? 'cursor-move' : 'pointer-events-none'}`}
          title={editMode ? 'Drag to move sensor on plan' : undefined}
        >
          <div
            className={`w-2.5 h-2.5 rounded-full border-2 ${
              isOffline ? 'bg-amber-500 border-amber-400'
                : isOutOfService || !isOnline ? 'bg-gray-700 border-gray-500'
                : preAlarm ? 'bg-orange-500 border-orange-400'
                : latestAlarm && isAlarmLow ? 'bg-blue-500 border-blue-400'
                : latestAlarm ? 'bg-red-600 border-red-500'
                : 'bg-emerald-600 border-emerald-500'
            }`}
          />
        </div>
      </div>
    </div>
  );
};

// ─── Unplaced sensors strip ───────────────────────────────────────────────────

interface UnplacedSensorsAreaProps {
  unplacedSensors: any[];
  onPlaceSensor: (sensor: any) => void;
  loading?: boolean;
}

const UnplacedSensorsArea: React.FC<UnplacedSensorsAreaProps> = ({
  unplacedSensors, onPlaceSensor, loading = false,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  if (!loading && unplacedSensors.length === 0) return null;

  return (
    <div className="bg-gray-800/70 border border-gray-700 rounded-xl mb-3 overflow-hidden">
      <button
        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-700/30 transition-colors"
        onClick={() => setCollapsed(c => !c)}
      >
        <MapPin className="w-4 h-4 text-amber-400 flex-shrink-0" />
        <span className="text-amber-400 font-medium">
          {loading ? 'Loading sensors…' : `${unplacedSensors.length} unplaced sensor${unplacedSensors.length !== 1 ? 's' : ''}`}
        </span>
        <span className="text-gray-500 text-xs">Click to place on plan</span>
        <span className="ml-auto">
          {collapsed
            ? <ChevronDown className="w-4 h-4 text-gray-500" />
            : <ChevronUp className="w-4 h-4 text-gray-500" />}
        </span>
      </button>

      {!collapsed && (
        <div className="flex flex-wrap gap-2 px-4 pb-3 pt-1">
          {unplacedSensors.map(sensor => (
            <button
              key={sensor.id}
              onClick={() => onPlaceSensor(sensor)}
              className="flex items-center gap-2 bg-gray-700/80 hover:bg-gray-700 border border-gray-600 hover:border-amber-500 rounded-lg px-3 py-2 transition-all text-left group"
            >
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${sensor.status === 'online' ? 'bg-emerald-400' : 'bg-gray-500'}`} />
              <div>
                <div className="text-white text-xs font-medium group-hover:text-amber-300 transition-colors">{sensor.name}</div>
                <div className="text-gray-400 text-[10px]">
                  {sensor.status === 'online' ? `${(sensor.temp ?? 0).toFixed(1)}°C · ${sensor.humidity ?? 0}%` : 'Offline'}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Site plan editor ─────────────────────────────────────────────────────────

interface SitePlanEditorProps {
  sensors: any[];
  onUpdateSensors: (sensors: any[]) => void;
  planImage: string | null;
  onUploadPlan: (file: File) => void;
  onSave: (sensors: any[], planImage: string | null) => void;
  selectedZoneId?: number | null;
  editMode: boolean;
  onEnterEditMode: () => void;
  onCancelEdit: () => void;
  /** Moves every placed sensor back to the unplaced panel without saving — lets the user start the layout over. */
  onUnplaceAll: () => void;
  isSlideshow?: boolean;
  slideshowDwell?: number;
  /** Raw `/alarms/alarm-log` body (may be an array or wrapped object). */
  alarmLog?: unknown;
  /** `/alarms` threshold config — used when log rows lag behind live readings. */
  alarmsFromApi?: any[];
  highlightedSensorId?: string | null;
  /** True while the zone plan + sensors are being fetched — shows a loading spinner in the canvas area. */
  planLoading?: boolean;
}

const SitePlanEditor: React.FC<SitePlanEditorProps> = ({
  sensors, onUpdateSensors, planImage, onUploadPlan, onSave, selectedZoneId,
  editMode, onEnterEditMode, onCancelEdit, onUnplaceAll, isSlideshow = false, slideshowDwell = 10,
  alarmLog,
  alarmsFromApi = [],
  highlightedSensorId,
  planLoading = false,
}) => {
  const [selectedSensor, setSelectedSensor] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [planOpacity, setPlanOpacity] = useState(1);

  useEffect(() => {
    const stored = localStorage.getItem(`planview_opacity_${selectedZoneId ?? ''}`);
    setPlanOpacity(stored ? Math.max(0.1, Math.min(1, parseFloat(stored))) : 1);
  }, [selectedZoneId]);

  const handleOpacityChange = (val: number) => {
    setPlanOpacity(val);
    localStorage.setItem(`planview_opacity_${selectedZoneId ?? ''}`, String(val));
  };
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [pseudoFullscreen, setPseudoFullscreen] = useState(false);
  /** True when native :fullscreen applies to containerRef (excludes pseudo-iPad overlay). */
  const [nativeFullscreenActive, setNativeFullscreenActive] = useState(false);
  const isFullscreen = pseudoFullscreen || nativeFullscreenActive;
  const [canvasSize, setCanvasSize] = useState({ w: 994, h: 500 });
  const [imgNaturalSize, setImgNaturalSize] = useState<{ w: number; h: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasViewportRef = useRef<HTMLDivElement>(null);
  const imageSurfaceRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const navigateToSensorChart = (sensorId: string) => {
    navigate(`/sensors/${sensorId}`);
  };

  // Track the image surface's actual rendered dimensions for drag calculations.
  // Re-attaches when planImage changes so the ref is populated.
  useEffect(() => {
    const el = imageSurfaceRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      if (width > 0 && height > 0) setCanvasSize({ w: width, h: height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [planImage]);

  const exitNativeFullscreen = async () => {
    try {
      if (!getDocFullscreenEl()) return;
      const docAny = document as Document & {
        exitFullscreen?: () => Promise<void>;
        webkitExitFullscreen?: () => Promise<void>;
      };
      if (typeof docAny.exitFullscreen === 'function') await docAny.exitFullscreen();
      else if (typeof docAny.webkitExitFullscreen === 'function') await docAny.webkitExitFullscreen();
    } catch {
      /* ignore */
    }
  };

  const enterNativeFullscreen = async (el: HTMLElement) => {
    const h = el as HTMLElement & {
      requestFullscreen?: (opts?: FullscreenOptions) => Promise<void>;
      webkitRequestFullscreen?: () => Promise<void>;
    };
    if (h.requestFullscreen) await h.requestFullscreen();
    else if (h.webkitRequestFullscreen) await h.webkitRequestFullscreen();
    else throw new Error('fullscreen unavailable');
  };

  const syncNativeFullscreenFlag = useCallback(() => {
    const shell = containerRef.current;
    const full = getDocFullscreenEl();
    setNativeFullscreenActive(!!shell && !!full && full === shell);
  }, []);

  const toggleFullscreen = async () => {
    const shell = containerRef.current;
    if (!shell) return;

    if (pseudoFullscreen) {
      setPseudoFullscreen(false);
      document.body.style.overflow = '';
      return;
    }

    if (getDocFullscreenEl() === shell) {
      await exitNativeFullscreen();
      return;
    }

    if (isIosLikeDevice()) {
      setPseudoFullscreen(true);
      document.body.style.overflow = 'hidden';
      return;
    }

    try {
      await enterNativeFullscreen(shell);
    } catch {
      setPseudoFullscreen(true);
      document.body.style.overflow = 'hidden';
    }
  };

  useEffect(() => {
    syncNativeFullscreenFlag();
    document.addEventListener('fullscreenchange', syncNativeFullscreenFlag);
    document.addEventListener('webkitfullscreenchange', syncNativeFullscreenFlag as EventListener);
    return () => {
      document.removeEventListener('fullscreenchange', syncNativeFullscreenFlag);
      document.removeEventListener('webkitfullscreenchange', syncNativeFullscreenFlag as EventListener);
    };
  }, [syncNativeFullscreenFlag]);

  useEffect(() => {
    if (!pseudoFullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPseudoFullscreen(false);
        document.body.style.overflow = '';
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pseudoFullscreen]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
      void exitNativeFullscreen();
    };
  }, []);

  // Clear selection when leaving edit mode
  useEffect(() => {
    if (!editMode) setSelectedSensor(null);
  }, [editMode]);

  const onlineCount       = sensors.filter(s => s.status === 'online').length;
  const offlineCount      = sensors.filter(s => s.status === 'offline').length;
  const outOfServiceCount = sensors.filter(s => s.status === 'out-of-service').length;
  const alarmOnPlanCount  = useMemo(
    () => sensors.filter(s => resolveSensorPlanAlarm(String(s.id), alarmLog, alarmsFromApi, s) != null).length,
    [sensors, alarmLog, alarmsFromApi]
  );

  const labelLayouts = useMemo(() => {
    const map = new Map<string, PlanSensorLabelLayout>();
    for (const s of sensors) {
      if (hasManualLabelOffset(s)) {
        map.set(
          s.id,
          layoutFromManualOffset(s.labelCenterOffsetX!, s.labelCenterOffsetY!),
        );
      } else {
        map.set(s.id, DEFAULT_PLAN_LABEL_LAYOUT);
      }
    }
    return map;
  }, [sensors]);

  const handleDrag = (id: string, x: number, y: number) =>
    onUpdateSensors(sensors.map(s => s.id === id ? { ...s, x, y } : s));

  const handleLabelDrag = (id: string, centerOffsetX: number, centerOffsetY: number) =>
    onUpdateSensors(sensors.map(s =>
      s.id === id ? { ...s, labelCenterOffsetX: centerOffsetX, labelCenterOffsetY: centerOffsetY } : s,
    ));

  const handleResetLabel = (id: string) =>
    onUpdateSensors(sensors.map(s => {
      if (s.id !== id) return s;
      const { labelCenterOffsetX: _x, labelCenterOffsetY: _y, ...rest } = s;
      return rest;
    }));

  const handleAutoArrangeLabels = () => {
    const auto = computePlanLabelLayouts(
      sensors.map((s) => ({ id: s.id, x: s.x, y: s.y, name: s.name ?? '' })),
      canvasSize.w,
      canvasSize.h,
    );
    onUpdateSensors(sensors.map(s => {
      const layout = auto.get(s.id);
      if (!layout) return s;
      return {
        ...s,
        labelCenterOffsetX: layout.centerOffsetX,
        labelCenterOffsetY: layout.centerOffsetY,
      };
    }));
  };

  const handleDelete = (id: string) => {
    onUpdateSensors(sensors.filter(s => s.id !== id));
    setSelectedSensor(null);
  };

  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-sensor-card]')) return;
    if (editMode) setSelectedSensor(null);
    setIsPanning(true);
    setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  useEffect(() => {
    if (!isPanning) return;
    const onMove = (e: MouseEvent) => setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
    const onUp   = () => setIsPanning(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isPanning, panStart]);

  const statusBar = sensors.length > 0 && (
    <div className={`flex flex-wrap items-center gap-3 md:gap-6 px-4 md:px-5 py-2.5 md:py-3 bg-gray-800/60 border border-gray-700 ${isFullscreen ? 'rounded-xl' : 'rounded-xl'}`}>
      <div className="flex items-center gap-2">
        <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-50" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
        </span>
        <span className="text-emerald-400 text-sm font-medium">{onlineCount} Online</span>
      </div>
      {offlineCount > 0 && (
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-60" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
          </span>
          <span className="text-amber-400 text-sm font-medium">{offlineCount} Offline</span>
        </div>
      )}
      {outOfServiceCount > 0 && (
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-gray-500 flex-shrink-0" />
          <span className="text-gray-400 text-sm">{outOfServiceCount} Out of service</span>
        </div>
      )}
      {alarmOnPlanCount > 0 && (
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-red-400 flex-shrink-0 bell-flash-red" />
          <span className="text-red-400 text-sm font-semibold">{alarmOnPlanCount} In alarm</span>
        </div>
      )}
      <div className="ml-auto text-gray-600 text-xs">{sensors.length} sensor{sensors.length !== 1 ? 's' : ''} on plan</div>
    </div>
  );

  return (
    <div
      ref={containerRef}
      className={`reading-boost-exempt plan-view-fs-root ${
        isFullscreen
          ? `flex flex-col bg-gray-950 min-h-0 ${
              pseudoFullscreen
                ? 'fixed inset-0 z-[9999] pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] box-border'
                : ''
            }`
          : 'flex flex-col gap-3'
      }`}
      style={isFullscreen && !pseudoFullscreen ? { height: '100%' } : undefined}
    >
      {/* Status bar — normal mode */}
      {!isFullscreen && statusBar}

      {/* Canvas */}
      <div
        className={`relative bg-gray-950 overflow-hidden ${
          isFullscreen ? 'flex-1 min-h-0' : 'rounded-2xl border border-gray-800 shadow-2xl'
        }`}
      >
        {/* Status bar overlay — fullscreen mode */}
        {isFullscreen && (
          <div className="absolute top-4 left-4 z-30 flex items-center">
            {statusBar}
          </div>
        )}

        {/* Exit fullscreen button — top left, fullscreen only */}
        {isFullscreen && (
          <button
            onClick={toggleFullscreen}
            className="absolute top-16 left-4 z-30 flex items-center gap-1.5 px-3 py-2 bg-gray-900/95 backdrop-blur-sm border border-gray-700 rounded-xl text-gray-300 hover:text-white hover:bg-gray-800 text-sm font-medium transition-colors shadow-xl"
            title="Exit fullscreen (Esc)"
          >
            <Minimize2 className="w-4 h-4" />
            Exit Fullscreen
          </button>
        )}

        {/* Floating toolbar — top right */}
        <div className="absolute top-4 right-4 z-30 flex items-center gap-1 bg-gray-900/95 backdrop-blur-sm border border-gray-700 rounded-xl px-2.5 py-2 shadow-xl max-w-[calc(100vw-2rem)] overflow-x-auto">
          <button onClick={() => setZoom(z => Math.max(0.25, z - 0.15))} className="p-1.5 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors" title="Zoom out">
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-gray-400 text-xs w-12 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => Math.min(4, z + 0.15))} className="p-1.5 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors" title="Zoom in">
            <ZoomIn className="w-4 h-4" />
          </button>
          <div className="w-px h-5 bg-gray-700 mx-1" />
          <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} className="p-1.5 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors" title="Reset view">
            <RotateCcw className="w-4 h-4" />
          </button>
          <div className="w-px h-5 bg-gray-700 mx-1" />
          {editMode ? (
            <>
              <button
                type="button"
                onClick={handleAutoArrangeLabels}
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-violet-700 hover:bg-violet-600 rounded-lg text-white text-sm font-medium transition-colors"
                title="Auto-arrange overlapping labels (optional)"
              >
                <Wand2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Arrange labels</span>
              </button>
              {planImage && (
                <>
                  <div className="w-px h-5 bg-gray-700 mx-1" />
                  <div className="flex items-center gap-1.5" title="Adjust plan image transparency">
                    <span className="text-gray-500 text-[11px] hidden sm:inline whitespace-nowrap">Opacity</span>
                    <input
                      type="range"
                      min={0.1}
                      max={1}
                      step={0.05}
                      value={planOpacity}
                      onChange={e => handleOpacityChange(parseFloat(e.target.value))}
                      className="w-20 accent-indigo-500 cursor-pointer"
                      title={`Plan opacity: ${Math.round(planOpacity * 100)}%`}
                    />
                    <span className="text-gray-400 text-xs w-7 text-right tabular-nums">{Math.round(planOpacity * 100)}%</span>
                  </div>
                </>
              )}
              <button
                onClick={() => onSave(sensors, planImage)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-600 hover:bg-blue-500 rounded-lg text-white text-sm font-medium transition-colors"
                title="Save Layout"
              >
                <Save className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Save Layout</span>
              </button>
              {sensors.length > 0 && (
                <button
                  onClick={() => {
                    if (window.confirm(`Move all ${sensors.length} placed sensors back to unplaced? This won't save until you click Save Layout.`)) {
                      onUnplaceAll();
                    }
                  }}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-700 hover:bg-amber-700 rounded-lg text-gray-300 hover:text-white text-sm font-medium transition-colors"
                  title="Move every placed sensor back to the unplaced panel"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Unplace All</span>
                </button>
              )}
              <button
                onClick={onCancelEdit}
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 text-sm font-medium transition-colors"
                title="Exit edit mode without saving"
              >
                <X className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Cancel</span>
              </button>
            </>
          ) : (
            <button
              onClick={onEnterEditMode}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-white text-sm font-medium transition-colors"
              title="Edit Layout"
            >
              <Pencil className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Edit Layout</span>
            </button>
          )}
          <div className="w-px h-5 bg-gray-700 mx-1" />
          <button
            onClick={toggleFullscreen}
            className="p-1.5 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors"
            title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'}
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>

        {/* Upload plan button — top left, only in edit mode */}
        {editMode && (
          <div className="absolute top-4 left-4 z-30">
            <label className="flex items-center gap-2 px-3 py-2 bg-gray-900/95 hover:bg-gray-800 border border-gray-700 rounded-xl cursor-pointer text-gray-300 text-sm transition-colors shadow-lg">
              <Upload className="w-4 h-4" />
              {planImage ? 'Change Plan' : 'Upload Plan'}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => e.target.files?.[0] && onUploadPlan(e.target.files[0])}
              />
            </label>
          </div>
        )}

        {/* Main canvas viewport */}
        <div
          ref={canvasViewportRef}
          className="site-plan-canvas relative w-full overflow-hidden"
          style={isFullscreen
            ? { height: '100%', minHeight: 0, cursor: isPanning ? 'grabbing' : 'grab' }
            : { height: 'calc(100vh - 290px)', minHeight: 280, cursor: isPanning ? 'grabbing' : 'grab' }
          }
          onMouseDown={handleCanvasMouseDown}
        >
          {isSlideshow && planImage && (
            <>
              <style>{`@keyframes slideshow-progress { from { width: 0% } to { width: 100% } }`}</style>
              <div className="absolute top-0 left-0 right-0 h-1 bg-gray-700/50 z-30 pointer-events-none overflow-hidden">
                <div
                  key={`sp-${selectedZoneId}`}
                  className="h-full bg-indigo-500"
                  style={{ animation: `slideshow-progress ${slideshowDwell}s linear forwards` }}
                />
              </div>
            </>
          )}
          <div
            style={{
              transform: `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`,
              transformOrigin: 'center center',
              width: '100%',
              height: '100%',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {planImage ? (
              /* imageSurface: locked to the image's aspect ratio so sensor %
                 coords are always relative to the actual image area, not the
                 surrounding container (which changes size in fullscreen). */
              <div
                ref={imageSurfaceRef}
                style={{
                  position: 'relative',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  flexShrink: 0,
                  ...(imgNaturalSize
                    ? { aspectRatio: `${imgNaturalSize.w} / ${imgNaturalSize.h}` }
                    : { width: '100%', height: '100%' }),
                }}
              >
                <img
                  src={planImage}
                  alt="Zone plan"
                  className="w-full h-full block pointer-events-none"
                  style={{ opacity: planOpacity }}
                  draggable={false}
                  onLoad={(e) => {
                    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                    setImgNaturalSize({ w, h });
                  }}
                />

                {/* Subtle grid */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
                    backgroundSize: '50px 50px',
                  }}
                />

                {sensors.map(sensor => (
                  <SitePlanSensor
                    key={sensor.id}
                    sensor={sensor}
                    latestAlarm={resolveSensorPlanAlarm(String(sensor.id), alarmLog, alarmsFromApi, sensor)}
                    onDrag={handleDrag}
                    onLabelDrag={handleLabelDrag}
                    onResetLabel={handleResetLabel}
                    onDelete={handleDelete}
                    isSelected={selectedSensor === sensor.id}
                    onSelect={setSelectedSensor}
                    zoom={zoom}
                    editMode={editMode}
                    canvasSize={canvasSize}
                    onNavigateToGraph={navigateToSensorChart}
                    isHighlighted={!!highlightedSensorId && sensor.id === highlightedSensorId}
                    labelLayout={labelLayouts.get(sensor.id) ?? DEFAULT_PLAN_LABEL_LAYOUT}
                    planOpacity={planOpacity}
                  />
                ))}
              </div>
            ) : planLoading ? (
              <div className="flex flex-col items-center justify-center h-full w-full gap-4">
                <svg className="animate-spin w-10 h-10 text-indigo-500" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                  <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
                <p className="text-gray-500 text-sm">Loading plan…</p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full w-full">
                <MapIcon className="w-24 h-24 text-gray-800 mb-5" />
                <p className="text-gray-600 text-xl font-medium">No floor plan uploaded</p>
                {editMode && (
                  <p className="text-gray-700 text-sm mt-2">Use the Upload Plan button above to add a floor plan image</p>
                )}
              </div>
            )}
          </div>
        </div>

        {editMode && planImage && sensors.length === 0 && (
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 bg-gray-900/80 backdrop-blur-sm border border-gray-700 rounded-xl px-5 py-2.5 text-gray-400 text-sm pointer-events-none text-center">
            Place sensors from the panel above, then Save Layout
          </div>
        )}

        {editMode && planImage && sensors.length > 0 && (
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 max-w-lg bg-gray-900/85 backdrop-blur-sm border border-gray-700 rounded-xl px-4 py-2 text-gray-400 text-xs pointer-events-none text-center">
            Drag the <span className="text-emerald-400">pin</span> to move a sensor · drag the <span className="text-indigo-300">label</span> to adjust its leader line · Save Layout when done
          </div>
        )}

        {/* View mode indicator */}
        {!editMode && (
          <div className="absolute bottom-4 left-4 z-20 bg-gray-900/80 backdrop-blur-sm border border-gray-700/60 rounded-lg px-3 py-1.5 text-gray-500 text-xs pointer-events-none">
            View mode — click Edit Layout to move sensors or change the plan
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function PlanViewPage() {
  const user = useSelector((state: RootState) => state.auth.user);
  const { pathname } = useLocation();
  const isActive = pathname === '/dashboard/plan';
  const [searchParams] = useSearchParams();
  const urlZoneParam = searchParams.get('zone');
  const urlHighlightParam = searchParams.get('highlight');
  const { data: alarmLogRaw } = useGetAlarmLogQuery(undefined, {
    skip: !user || !isActive,
    pollingInterval: 10000,
  });
  const { data: alarmsFromApi } = useGetAlarmsQuery(undefined, {
    skip: !user,
    refetchOnMountOrArgChange: false,
  });
  // 60-minute window: only show offline if no data received in the last hour
  const { data: rtkLiveData = [] } = useGetLiveDataQuery(
    { timeWindow: 60 },
    { skip: !user || !isActive, pollingInterval: 30000 },
  );
  const [zones, setZones]                         = useState<any[]>([]);
  const [selectedZoneId, setSelectedZoneId]       = useState<number | null>(urlZoneParam ? Number(urlZoneParam) : null);
  const [highlightedSensorId, setHighlightedSensorId] = useState<string | null>(urlHighlightParam);
  const [favouriteZoneId, setFavouriteZoneId]     = useState<number | null>(null);
  const [planImage, setPlanImage]                 = useState<string | null>(null);
  const [sensors, setSensors]                     = useState<any[]>([]);
  const [unplacedSensors, setUnplacedSensors]     = useState<any[]>([]);
  const [unplacedSensorsLoading, setUnplacedSensorsLoading] = useState(false);
  const [loading, setLoading]                     = useState(false);
  const [planLoading, setPlanLoading]             = useState(false);
  const [liveData, setLiveData]                   = useState<any[]>([]);
  const [isEditMode, setIsEditMode]               = useState(false);
  const [isSlideshow, setIsSlideshow]             = useState(false);
  const [slideshowDwell, setSlideshowDwell]       = useState(10);
  const [showSlideshowSettings, setShowSlideshowSettings] = useState(false);
  const [slideshowZones, setSlideshowZones]       = useState<number[]>([]);
  const [slideshowBuilding, setSlideshowBuilding] = useState(false);
  /** List position for slideshow advance — avoids indexOf() when duplicate zone IDs slipped through */
  const slideshowIdxRef                             = useRef(0);
  const liveDataRef = useRef<any[]>([]);
  useEffect(() => { liveDataRef.current = liveData; }, [liveData]);

  const alarmLog = useMemo(
    () => filterAlarmLogsBySensorAccess(alarmLogRaw, sensors.map((s) => ({ id: s.id }))),
    [alarmLogRaw, sensors],
  );

  // Load favourite from localStorage on mount
  useEffect(() => {
    if (!user?.id) return;
    const stored = localStorage.getItem(getFavouriteKey(user));
    if (stored) setFavouriteZoneId(Number(stored));
  }, [user?.id]);

  // Auto-clear highlight after 8 s once sensors are loaded
  useEffect(() => {
    if (!highlightedSensorId || sensors.length === 0) return;
    const t = setTimeout(() => setHighlightedSensorId(null), 8000);
    return () => clearTimeout(t);
  }, [highlightedSensorId, sensors.length]);

  const toggleFavourite = () => {
    if (!user?.id || !selectedZoneId) return;
    const key = getFavouriteKey(user);
    if (favouriteZoneId === selectedZoneId) {
      localStorage.removeItem(key);
      setFavouriteZoneId(null);
    } else {
      localStorage.setItem(key, String(selectedZoneId));
      setFavouriteZoneId(selectedZoneId);
    }
  };

  // Refresh unplaced sensors whenever zone or placed sensors change (only in edit mode)
  useEffect(() => {
    if (!user || !selectedZoneId || !isEditMode) return;

    const loadUnplacedSensors = async () => {
      setUnplacedSensorsLoading(true);
      try {
        const res  = await fetch(`${API_BASE}/zones/${selectedZoneId}/sensors`, { headers: getHeaders(user), credentials: 'include' });
        const data = await res.json();
        const unplaced: any[] = [];
        data.forEach((sensor: any) => {
          if (!sensors.some(p => p.id === String(sensor.sensor_id))) {
            const live = pickLiveRow(liveData, {
              id: String(sensor.sensor_id),
              serialNo: sensor.sensor_serialNo,
            });
            const oos = sensor.is_out_of_service === true;
            unplaced.push({
              id:       String(sensor.sensor_id),
              name:     sensor.sensor_name,
              serialNo: sensor.sensor_serialNo,
              temp:     live ? (parseFloat(live.temperature) || 0) : 0,
              humidity: live ? (parseFloat(live.humidity)    || 0) : 0,
              status:   oos ? 'out-of-service' : (live ? 'online' : 'offline'),
              is_out_of_service: oos,
              battery:  live?.Battery ?? 'N/A',
              rssi:     live?.RSSI ?? 'N/A',
            });
          }
        });
        setUnplacedSensors(unplaced);
      } catch {
        setUnplacedSensors([]);
      } finally {
        setUnplacedSensorsLoading(false);
      }
    };

    loadUnplacedSensors();
  }, [user, selectedZoneId, liveData, isEditMode]);

  // Save last-visited zone whenever it changes
  useEffect(() => {
    if (!user?.id || !selectedZoneId) return;
    localStorage.setItem(getLastZoneKey(user), String(selectedZoneId));
  }, [user, selectedZoneId]);

  // Fetch zones on mount; auto-select starred zone, then last-visited, then first available
  useEffect(() => {
    if (!user) return;
    setLoading(true);
    fetch(`${API_BASE}/zones`, { headers: getHeaders(user), credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        const list = data || [];
        setZones(list);
        if (!selectedZoneId && user?.id) {
          // 1. Try starred zone
          const favStored = localStorage.getItem(getFavouriteKey(user));
          if (favStored) {
            const favId = Number(favStored);
            if (list.some((z: any) => z.zone_id === favId)) { setSelectedZoneId(favId); return; }
          }
          // 2. Try last-visited zone
          const lastStored = localStorage.getItem(getLastZoneKey(user));
          if (lastStored) {
            const lastId = Number(lastStored);
            if (list.some((z: any) => z.zone_id === lastId)) { setSelectedZoneId(lastId); return; }
          }
          // 3. Fall back to first zone
          if (list.length > 0) setSelectedZoneId(list[0].zone_id);
        }
      })
      .catch(() => setZones([]))
      .finally(() => setLoading(false));
  }, [user]);

  // Keep placed sensors' live readings in sync whenever liveData updates
  useEffect(() => {
    if (liveData.length === 0) return;
    setSensors(prev => prev.map(sensor => {
      const live = pickLiveRow(liveData, sensor);
      const oos = sensor.is_out_of_service === true || (live && live.is_out_of_service === true);
      return {
        ...sensor,
        temp:     live ? (parseFloat(live.temperature) || 0) : sensor.temp,
        humidity: live ? (parseFloat(live.humidity)    || 0) : sensor.humidity,
        status:   oos ? 'out-of-service' : (live ? 'online' : 'offline'),
        is_out_of_service: oos,
        battery:  live?.Battery ?? sensor.battery,
        rssi:     live?.RSSI    ?? sensor.rssi,
      };
    }));
  }, [liveData]);

  // Sync RTK live data into local state so zone-load helpers and status effects see it
  useEffect(() => {
    if (rtkLiveData.length > 0) setLiveData(rtkLiveData);
  }, [rtkLiveData]);

  // Advance to the next zone during slideshow (only zones pre-confirmed to have a plan)
  useEffect(() => {
    if (!isSlideshow || loading || slideshowZones.length < 2) return;
    const timer = setTimeout(() => {
      const nextIdx = (slideshowIdxRef.current + 1) % slideshowZones.length;
      slideshowIdxRef.current = nextIdx;
      setSelectedZoneId(slideshowZones[nextIdx]);
    }, slideshowDwell * 1000);
    return () => clearTimeout(timer);
  }, [isSlideshow, loading, selectedZoneId, slideshowZones, slideshowDwell]);

  // Keep ref aligned when the displayed zone matches slideshow list (manual dropdown / external change)
  useEffect(() => {
    if (!isSlideshow || slideshowZones.length < 1) return;
    const i = slideshowZones.indexOf(selectedZoneId ?? -1);
    if (i >= 0) slideshowIdxRef.current = i;
  }, [isSlideshow, selectedZoneId, slideshowZones]);

  // Load plan + sensors when zone changes
  useEffect(() => {
    if (!user || !selectedZoneId) { setPlanImage(null); setSensors([]); setPlanLoading(false); return; }
    setPlanLoading(true);
    setPlanImage(null);
    setSensors([]);
    setIsEditMode(false);

    // Normalise legacy absolute-pixel values to relative (0-1); modern saves are already relative
    const toRelative = (posX: number, posY: number) => ({
      x: posX > 1 ? posX / 994 : posX,
      y: posY > 1 ? posY / 384 : posY,
    });

    // Helper: apply live data to a list of sensors
    // temperature/humidity come back as strings from the API — parse to numbers
    const withLive = (list: any[], currentLiveData: any[]) =>
      list.map(sensor => {
        const live = pickLiveRow(currentLiveData, sensor);
        const oos = sensor.is_out_of_service === true || (live && live.is_out_of_service === true);
        return {
          ...sensor,
          temp:     live ? (parseFloat(live.temperature) || 0) : (sensor.temp ?? 0),
          humidity: live ? (parseFloat(live.humidity)    || 0) : (sensor.humidity ?? 0),
          status:   oos ? 'out-of-service' : (live ? 'online' : 'offline'),
          is_out_of_service: oos,
          battery:  live?.Battery ?? sensor.battery ?? 'N/A',
          rssi:     live?.RSSI    ?? sensor.rssi    ?? 'N/A',
        };
      });

    const loadExistingPlan = async (currentLiveData: any[], result: any): Promise<boolean> => {
      try {
        if (!result.success || !result.data) return false;

        if (result.data.imageData) {
          const url = result.data.imageData.startsWith('data:')
            ? result.data.imageData
            : `data:image/${result.data.imageFormat || 'png'};base64,${result.data.imageData}`;
          setPlanImage(url);
        }

        const sensorPositions: any[] = (result.data.sensorPositions ?? [])
          .filter((pos: any, idx: number, arr: any[]) =>
            arr.findIndex(p => p.sensorId === pos.sensorId) === idx);

        if (sensorPositions.length > 0) {
          // Always fetch zone sensors so we have the authoritative serialNo for each
          // sensor by its database ID — the stored sensorSerialNo in plan positions
          // may be missing or stale if the plan was saved before serialNo was tracked.
          const serialNoByDbId: Record<string, string> = {};
          const oosByDbId: Record<string, boolean> = {};
          try {
            const sensRes = await fetch(`${API_BASE}/zones/${selectedZoneId}/sensors`, { headers: getHeaders(user), credentials: 'include' });
            const sensData = await sensRes.json();
            if (Array.isArray(sensData)) {
              sensData.forEach((s: any) => {
                serialNoByDbId[String(s.sensor_id)] = String(s.sensor_serialNo);
                oosByDbId[String(s.sensor_id)] = s.is_out_of_service === true;
              });
            }
          } catch { /* proceed without — matching will fall back to stored value */ }

          const mapped = sensorPositions.map((pos: any) => {
            const { x, y } = toRelative(pos.positionX, pos.positionY);
            // Prefer live lookup; fall back to whatever was saved in the plan JSON
            const serialNo = serialNoByDbId[String(pos.sensorId)] ?? pos.sensorSerialNo;
            const oos = oosByDbId[String(pos.sensorId)] === true;
            return {
              id:       String(pos.sensorId),
              name:     pos.sensorName || `Sensor ${pos.sensorId}`,
              serialNo,
              temp:     0,
              humidity: 0,
              status:   oos ? 'out-of-service' : 'offline',
              is_out_of_service: oos,
              battery:  'N/A',
              rssi:     'N/A',
              x, y,
              size:     pos.sensorSize || 32,
              rotation: pos.rotation   || 0,
              isVisible: pos.isVisible !== false,
              ...(pos.labelCenterOffsetX != null && pos.labelCenterOffsetY != null
                ? {
                    labelCenterOffsetX: Number(pos.labelCenterOffsetX),
                    labelCenterOffsetY: Number(pos.labelCenterOffsetY),
                  }
                : {}),
            };
          });
          setSensors(withLive(mapped, currentLiveData));
          setPlanLoading(false);
          return true;
        }
        setPlanLoading(false);
        return false;
      } catch { return false; }
    };

    // savedPositions passed in from the IIFE so we don't hit getZonePlan twice
    const loadSensorsFromApi = async (currentLiveData: any[], savedPositions: any[] = []) => {
      try {
        const res  = await fetch(`${API_BASE}/zones/${selectedZoneId}/sensors`, { headers: getHeaders(user), credentials: 'include' });
        const data = await res.json();
        if (!Array.isArray(data)) { setSensors([]); setPlanLoading(false); return; }

        const placed: any[] = [];
        const unplaced: any[] = [];
        data.forEach((sensor: any) => {
          const savedPos = savedPositions.find((p: any) => p.sensorId === sensor.sensor_id);
          const oos = sensor.is_out_of_service === true;
          const base = {
            id:       String(sensor.sensor_id),
            name:     sensor.sensor_name,
            serialNo: sensor.sensor_serialNo,
            temp:     0,
            humidity: 0,
            status:   oos ? 'out-of-service' : 'offline',
            is_out_of_service: oos,
            battery:  'N/A',
            rssi:     'N/A',
          };
          if (savedPos) {
            const { x, y } = toRelative(savedPos.positionX, savedPos.positionY);
            placed.push({
              ...base,
              x,
              y,
              ...(savedPos.labelCenterOffsetX != null && savedPos.labelCenterOffsetY != null
                ? {
                    labelCenterOffsetX: Number(savedPos.labelCenterOffsetX),
                    labelCenterOffsetY: Number(savedPos.labelCenterOffsetY),
                  }
                : {}),
            });
          } else {
            unplaced.push(base);
          }
        });

        setSensors(withLive(placed, currentLiveData));
        setUnplacedSensors(withLive(unplaced, currentLiveData));
      } catch (err) {
        console.error('Error fetching zone sensors:', err);
        setSensors([]);
        setUnplacedSensors([]);
      } finally {
        setPlanLoading(false);
      }
    };

    (async () => {
      try {
        // Ensure live data is available before mapping sensor status.
        // If we don't have it yet, fetch it now so sensors load with correct online/offline state.
        let currentLiveData = liveDataRef.current;
        if (currentLiveData.length === 0) {
          try {
            const liveRes = await fetch(`${API_BASE}/getLiveData?timeWindow=60`, { headers: getHeaders(user), credentials: 'include' });
            if (liveRes.ok) {
              currentLiveData = (await liveRes.json()) || [];
              setLiveData(currentLiveData);
              liveDataRef.current = currentLiveData;
            }
          } catch { /* proceed without */ }
        }

        // Fetch the plan once and share the result with both functions
        const planResult = await zonePlanApi.getZonePlan(selectedZoneId, user);
        const savedPositions = (planResult.success && planResult.data?.sensorPositions) ? planResult.data.sensorPositions : [];
        const planLoaded = await loadExistingPlan(currentLiveData, planResult);
        if (!planLoaded) await loadSensorsFromApi(currentLiveData, savedPositions);
      } catch (err) {
        console.error('Error loading zone plan:', err);
        await loadSensorsFromApi(liveDataRef.current, []);
      }
    })();
  }, [user, selectedZoneId]);

  const handleUploadPlan = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = (e.target?.result as string).split(',')[1];
      setPlanImage(`data:image/png;base64,${base64}`);
    };
    reader.readAsDataURL(file);
  };

  const handlePlaceSensor = (sensor: any) => {
    setUnplacedSensors(prev => prev.filter(s => s.id !== sensor.id));
    setSensors(prev => [...prev, { ...sensor, x: 0.5, y: 0.5 }]);
  };

  const handleUnplaceAll = () => {
    setUnplacedSensors(prev => {
      const merged = [...prev];
      sensors.forEach(sensor => {
        if (!merged.some(s => s.id === sensor.id)) merged.push(sensor);
      });
      return merged;
    });
    setSensors([]);
  };

  const handleSave = async (updatedSensors: any[], planImageStr: string | null) => {
    if (!user || !selectedZoneId) return;
    setLoading(true);
    try {
      const sensorPositions = updatedSensors.map(sensor => ({
        sensorId:    Number(sensor.id),
        sensorName:  sensor.name,
        sensorSerialNo: sensor.serialNo,
        positionX:   sensor.x,
        positionY:   sensor.y,
        sensorSize:  sensor.size     || 32,
        rotation:    sensor.rotation || 0,
        isVisible:   sensor.isVisible !== false,
        ...(sensor.labelCenterOffsetX != null && sensor.labelCenterOffsetY != null
          ? {
              labelCenterOffsetX: sensor.labelCenterOffsetX,
              labelCenterOffsetY: sensor.labelCenterOffsetY,
            }
          : {}),
      }));

      if (planImageStr) {
        const result = await zonePlanApi.saveZonePlan({
          zoneId:       selectedZoneId,
          planName:     zones.find(z => z.zone_id === selectedZoneId)?.zone_name || `Zone ${selectedZoneId} Plan`,
          planDescription: `Plan for ${zones.find(z => z.zone_id === selectedZoneId)?.zone_name || 'Zone'}`,
          imageData:    planImageStr,
          imageFormat:  'png',
          scale:        1.0,
          sensorPositions,
        }, user);

        if (result.success) {
          setSensors(updatedSensors);
          setIsEditMode(false);
          alert('Zone plan saved successfully!');
        } else {
          alert('Save failed: ' + result.error);
        }
      } else {
        const result = await zonePlanApi.updateSensorPositions(selectedZoneId, sensorPositions, user);
        if (result.success) {
          setSensors(updatedSensors);
          setIsEditMode(false);
          alert('Sensor positions updated!');
        } else {
          alert('Update failed: ' + result.error);
        }
      }
    } catch (err) {
      console.error('Error saving zone plan:', err);
      alert('Save failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const selectedZone = zones.find(z => z.zone_id === selectedZoneId);
  const isDefaultZone = favouriteZoneId === selectedZoneId;

  return (
    <div className="p-4 pb-6">
      {/* Header */}
      <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/"
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-300 transition-colors flex-shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2 truncate">
              Plan View
              {selectedZone && (
                <span className="text-gray-600 dark:text-gray-400 font-normal text-sm sm:text-base truncate">— {selectedZone.zone_name}</span>
              )}
            </h2>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={selectedZoneId || ''}
            onChange={e => setSelectedZoneId(e.target.value ? Number(e.target.value) : null)}
            className="block w-full sm:w-56 pl-3 pr-8 py-2 text-sm border border-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-500 rounded-lg bg-gray-800 text-gray-100"
          >
            <option value="">Select Zone</option>
            {zones.map(zone => (
              <option key={zone.zone_id} value={zone.zone_id}>
                {zone.zone_name} ({zone.sensor_count} sensors)
              </option>
            ))}
          </select>
          {selectedZoneId && (
            <button
              onClick={toggleFavourite}
              title={isDefaultZone ? 'Remove as default zone' : 'Set as default — auto-loads on next visit'}
              className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg border transition-colors text-sm font-medium ${
                isDefaultZone
                  ? 'bg-yellow-500/15 border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/25'
                  : 'bg-gray-800 border-gray-600 text-gray-400 hover:text-yellow-400 hover:border-yellow-500/40'
              }`}
            >
              <Star className={`w-4 h-4 ${isDefaultZone ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'}`} />
              {isDefaultZone ? 'Default' : 'Set Default'}
            </button>
          )}

          {/* Slideshow */}
          <div className="relative">
            {isSlideshow ? (
              <button
                onClick={() => { slideshowIdxRef.current = 0; setIsSlideshow(false); setSlideshowZones([]); }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-500/40 bg-red-500/15 text-red-400 hover:bg-red-500/25 transition-colors text-sm font-medium"
              >
                <Square className="w-4 h-4" />
                Stop
              </button>
            ) : (
              <button
                onClick={() => setShowSlideshowSettings(s => !s)}
                title="Auto-cycle through zones with a site plan"
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-colors text-sm font-medium ${
                  showSlideshowSettings
                    ? 'bg-indigo-500/20 border-indigo-500/50 text-indigo-400'
                    : 'bg-gray-800 border-gray-600 text-gray-300 hover:text-white hover:border-gray-400'
                }`}
              >
                <Play className="w-4 h-4" />
                Slideshow
              </button>
            )}
            {showSlideshowSettings && !isSlideshow && (
              <div className="absolute right-0 top-full mt-2 z-50 bg-gray-800 border border-gray-600 rounded-xl shadow-xl p-4 w-60">
                <div className="text-gray-200 text-sm font-medium mb-3">Slideshow</div>
                <label className="block text-gray-400 text-xs mb-1">Seconds per zone</label>
                <input
                  type="number"
                  min={3} max={300} step={1}
                  value={slideshowDwell}
                  onChange={e => setSlideshowDwell(Math.max(3, Math.min(300, Number(e.target.value))))}
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-1.5 text-sm mb-3 focus:outline-none focus:border-indigo-500"
                />
                <button
                  onClick={async () => {
                    setShowSlideshowSettings(false);
                    setSlideshowBuilding(true);
                    const r = await zonePlanApi.listZonesWithPlans(user);
                    setSlideshowBuilding(false);
                    if (!r.success) {
                      alert(r.error ?? 'Could not load zones with site plans.');
                      return;
                    }
                    const withPlans = r.data ?? [];
                    if (withPlans.length === 0) {
                      alert('No zones have a site plan configured.');
                      return;
                    }
                    setSlideshowZones(withPlans);
                    const startId = withPlans.includes(selectedZoneId ?? -1) ? selectedZoneId! : withPlans[0];
                    slideshowIdxRef.current = Math.max(0, withPlans.indexOf(startId));
                    setIsSlideshow(true);
                    setSelectedZoneId(startId);
                  }}
                  disabled={zones.length < 1 || slideshowBuilding}
                  className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg py-1.5 text-sm font-medium transition-colors"
                >
                  {slideshowBuilding ? 'Checking zones…' : 'Start'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {loading && !selectedZoneId && (
        <div className="flex items-center justify-center p-12">
          <div className="text-gray-500 text-sm">Loading…</div>
        </div>
      )}

      {!selectedZoneId && !loading && (
        <div className="bg-gray-900 rounded-2xl border border-gray-800 flex flex-col items-center justify-center text-center"
          style={{ height: 'calc(100vh - 180px)', minHeight: 260 }}>
          <MapIcon className="w-20 h-20 mb-5 text-gray-700" />
          <h3 className="text-white text-xl font-semibold mb-2">Select a Zone</h3>
          <p className="text-gray-500 mb-2 max-w-sm">Choose a zone from the dropdown above to view its sensor layout and live readings</p>
          <p className="text-gray-600 text-sm mb-5">
            <Star className="w-3.5 h-3.5 inline mr-1 fill-yellow-500 text-yellow-500" />
            Use <span className="text-gray-400 font-medium">Set Default</span> to auto-load your preferred zone next visit
          </p>
          {zones.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 max-w-lg">
              {zones.map(z => (
                <button
                  key={z.zone_id}
                  onClick={() => setSelectedZoneId(z.zone_id)}
                  className={`px-3 py-1.5 border rounded-lg text-sm transition-colors ${
                    favouriteZoneId === z.zone_id
                      ? 'bg-yellow-500/10 border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/20'
                      : 'bg-gray-800 hover:bg-gray-700 border-gray-700 hover:border-gray-500 text-gray-300'
                  }`}
                >
                  {favouriteZoneId === z.zone_id && <Star className="w-3 h-3 inline mr-1 fill-yellow-400 text-yellow-400" />}
                  {z.zone_name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {selectedZoneId && (
        <>
          {isEditMode && (
            <UnplacedSensorsArea
              unplacedSensors={unplacedSensors}
              onPlaceSensor={handlePlaceSensor}
              loading={unplacedSensorsLoading}
            />
          )}
          <SitePlanEditor
            sensors={sensors}
            onUpdateSensors={setSensors}
            planImage={planImage}
            onUploadPlan={handleUploadPlan}
            onSave={handleSave}
            selectedZoneId={selectedZoneId}
            editMode={isEditMode}
            onEnterEditMode={() => { slideshowIdxRef.current = 0; setIsEditMode(true); setIsSlideshow(false); setSlideshowZones([]); }}
            onCancelEdit={() => setIsEditMode(false)}
            onUnplaceAll={handleUnplaceAll}
            isSlideshow={isSlideshow}
            slideshowDwell={slideshowDwell}
            alarmLog={alarmLog}
            alarmsFromApi={alarmsFromApi ?? []}
            highlightedSensorId={highlightedSensorId}
            planLoading={planLoading}
          />
        </>
      )}
    </div>
  );
}
